<template>
  <div class="d-flex flex-column ga-4">
    <div v-if="!inlineMode" class="ncrm-external-form-editor__grid">
      <v-text-field
        v-model.number="form.displayConfig.delaySeconds"
        type="number"
        min="0"
        max="600"
        suffix="s"
        :label="$t('ncrm.workflows.nodeSettings.externalForms.delay')"
        hide-details="auto"
      />
      <v-select
        v-model="form.displayConfig.repeatMode"
        :items="repeatModeOptions"
        item-title="label"
        item-value="value"
        :label="$t('ncrm.workflows.nodeSettings.externalForms.repeatMode')"
        hide-details="auto"
        :menu-props="selectMenuProps"
      />
      <v-text-field
        v-model.number="form.displayConfig.repeatAfterHours"
        type="number"
        min="1"
        max="720"
        suffix="h"
        :label="$t('ncrm.workflows.nodeSettings.externalForms.repeatAfterHours')"
        hide-details="auto"
      />
    </div>

    <v-row v-if="!inlineMode || fileRequestMode" dense>
      <v-col cols="12" md="4">
        <v-text-field
          v-model="form.theme.primaryColor"
          type="color"
          :label="$t('ncrm.workflows.nodeSettings.externalForms.primaryColor')"
          hide-details="auto"
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-text-field
          v-model="form.theme.accentColor"
          type="color"
          :label="$t('ncrm.workflows.nodeSettings.externalForms.accentColor')"
          hide-details="auto"
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-text-field
          v-model="form.theme.backgroundColor"
          type="color"
          :label="$t('ncrm.workflows.nodeSettings.externalForms.backgroundColor')"
          hide-details="auto"
        />
      </v-col>
    </v-row>

    <v-textarea
      v-model="originsValue"
      :label="$t('ncrm.workflows.nodeSettings.externalForms.allowedOrigins')"
      rows="2"
      hide-details="auto"
    />
    <v-row dense>
      <v-col cols="12" md="6">
        <v-textarea
          v-model="includePagesValue"
          :label="$t('ncrm.workflows.nodeSettings.externalForms.includePages')"
          rows="2"
          hide-details="auto"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-textarea
          v-model="excludePagesValue"
          :label="$t('ncrm.workflows.nodeSettings.externalForms.excludePages')"
          rows="2"
          hide-details="auto"
        />
      </v-col>
    </v-row>

    <v-divider />

    <div v-if="!inlineMode" class="text-body-2 font-weight-semibold ncrm-text-strong">
      {{ $t("ncrm.workflows.nodeSettings.externalForms.contentTitle") }}
    </div>
    <v-row v-if="!inlineMode" dense>
      <v-col cols="12" md="6">
        <v-text-field v-model="form.content.eyebrow" :label="$t('ncrm.workflows.nodeSettings.externalForms.eyebrow')" hide-details="auto" />
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field v-model="form.content.title" :label="$t('ncrm.workflows.nodeSettings.externalForms.title')" hide-details="auto" />
      </v-col>
      <v-col cols="12">
        <v-text-field v-model="form.content.titleAccent" :label="$t('ncrm.workflows.nodeSettings.externalForms.titleAccent')" hide-details="auto" />
      </v-col>
      <v-col cols="12">
        <v-textarea v-model="form.content.subtitle" :label="$t('ncrm.workflows.nodeSettings.externalForms.subtitle')" rows="2" hide-details="auto" />
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field v-model="form.content.commentLabel" :label="$t('ncrm.workflows.nodeSettings.externalForms.commentLabel')" hide-details="auto" />
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field v-model="form.content.commentPlaceholder" :label="$t('ncrm.workflows.nodeSettings.externalForms.commentPlaceholder')" hide-details="auto" />
      </v-col>
    </v-row>

    <v-row v-if="!fileRequestMode" dense>
      <v-col v-if="!inlineMode" cols="12" md="6">
        <v-switch v-model="form.fields.comment.enabled" color="primary" hide-details :label="$t('ncrm.workflows.nodeSettings.externalForms.countryEnabled')" />
      </v-col>
      <v-col cols="12">
        <v-checkbox v-model="form.consent.required" hide-details :label="$t('ncrm.workflows.nodeSettings.externalForms.consentRequired')" />
        <v-textarea v-model="form.consent.label" :label="$t('ncrm.workflows.nodeSettings.externalForms.consentText')" rows="2" hide-details="auto" />
      </v-col>
    </v-row>

    <v-divider v-if="!inlineMode" />

    <div v-if="!inlineMode" class="text-body-2 font-weight-semibold ncrm-text-strong">
      {{ $t("ncrm.workflows.nodeSettings.externalForms.channelsTitle") }}
    </div>
    <v-expansion-panels v-if="!inlineMode" variant="accordion">
      <v-expansion-panel v-for="channel in channelRows" :key="channel.key">
        <v-expansion-panel-title>
          <div class="d-flex align-center ga-2">
            <v-icon :color="channel.color">{{ channel.icon }}</v-icon>
            <span>{{ channel.label }}</span>
            <v-chip v-if="form.channels[channel.key].enabled" size="x-small" color="success">
              {{ $t("ncrm.workflows.nodeSettings.externalForms.enabled") }}
            </v-chip>
          </div>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-switch v-model="form.channels[channel.key].enabled" color="primary" hide-details :label="$t('ncrm.workflows.nodeSettings.externalForms.enabled')" />
          <v-row dense>
            <v-col cols="12" md="6">
              <v-text-field v-model="form.channels[channel.key].label" :label="$t('ncrm.workflows.nodeSettings.externalForms.channelLabel')" hide-details="auto" />
            </v-col>
            <v-col v-if="channel.key === 'whatsapp'" cols="12" md="6">
              <v-text-field v-model="form.channels.whatsapp.phone" label="WhatsApp phone" hide-details="auto" />
            </v-col>
            <v-col v-if="channel.key === 'whatsapp'" cols="12">
              <v-textarea v-model="form.channels.whatsapp.message" label="WhatsApp message" rows="2" hide-details="auto" />
            </v-col>
            <v-col v-if="channel.key === 'telegram'" cols="12">
              <v-text-field v-model="form.channels.telegram.link" label="Telegram link / username" hide-details="auto" />
            </v-col>
            <v-col v-if="channel.key === 'viber'" cols="12" md="6">
              <v-text-field v-model="form.channels.viber.phone" label="Viber phone" hide-details="auto" />
            </v-col>
            <v-col v-if="channel.key === 'viber'" cols="12" md="6">
              <v-text-field v-model="form.channels.viber.link" label="Viber link" hide-details="auto" />
            </v-col>
            <v-col v-if="channel.key === 'email'" cols="12" md="6">
              <v-text-field v-model="form.channels.email.address" label="Email address" hide-details="auto" />
            </v-col>
            <v-col v-if="channel.key === 'email'" cols="12" md="6">
              <v-text-field v-model="form.channels.email.subject" label="Email subject" hide-details="auto" />
            </v-col>
            <v-col v-if="channel.key === 'email'" cols="12">
              <v-textarea v-model="form.channels.email.body" label="Email body" rows="2" hide-details="auto" />
            </v-col>
          </v-row>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
export default {
  name: "NcrmWorkflowExternalFormEditor",
  props: {
    form: {
      type: Object,
      required: true,
    },
    originsText: {
      type: String,
      default: "",
    },
    includePagesText: {
      type: String,
      default: "",
    },
    excludePagesText: {
      type: String,
      default: "",
    },
    repeatModeOptions: {
      type: Array,
      default: () => [],
    },
    selectMenuProps: {
      type: Object,
      default: () => ({}),
    },
    inlineMode: {
      type: Boolean,
      default: false,
    },
    fileRequestMode: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:originsText", "update:includePagesText", "update:excludePagesText"],
  computed: {
    originsValue: {
      get() {
        return this.originsText;
      },
      set(value) {
        this.$emit("update:originsText", value);
      },
    },
    includePagesValue: {
      get() {
        return this.includePagesText;
      },
      set(value) {
        this.$emit("update:includePagesText", value);
      },
    },
    excludePagesValue: {
      get() {
        return this.excludePagesText;
      },
      set(value) {
        this.$emit("update:excludePagesText", value);
      },
    },
    channelRows() {
      return [
        { key: "whatsapp", label: "WhatsApp", icon: "mdi-whatsapp", color: "success" },
        { key: "telegram", label: "Telegram", icon: "mdi-send-circle-outline", color: "info" },
        { key: "viber", label: "Viber", icon: "mdi-phone-message-outline", color: "deep-purple" },
        { key: "email", label: "Email", icon: "mdi-email-outline", color: "primary" },
      ];
    },
  },
};
</script>

<style scoped>
.ncrm-external-form-editor__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

@media (max-width: 760px) {
  .ncrm-external-form-editor__grid {
    grid-template-columns: 1fr;
  }
}
</style>
